"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Globe, Search, Code2, FileText, LineChart, Clock, CheckCircle2, ArrowRight, Sparkles, Layers, ChevronRight, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processSteps = [{
  id: "step-1",
  stepNumber: "01",
  title: "Market Research & Region Prioritisation",
  shortTitle: "Market Research",
  timeline: "Scope dependent",
  icon: Search,
  tagline: "Data-led analysis before expanding internationally",
  description: "I evaluate cross-border search volume, local intent, and competitor depth across regions. I identify where your business has the strongest opportunity to generate qualified visits and commercial return, ensuring you avoid committing resources to oversaturated or low-intent foreign markets.",
  keyDeliverables: ["International Search Opportunity Analysis", "Target Market Prioritisation Matrix", "Local Competitor Landscape Review", "Cross-Border Commercial Intent Mapping"],
  consultantNote: "I analyse search demand and competitor strength first, validating each target market before committing resources."
}, {
  id: "step-2",
  stepNumber: "02",
  title: "Language & Regional Strategy",
  shortTitle: "Language Strategy",
  timeline: "Scope dependent",
  icon: Globe,
  tagline: "Structuring sites for multilingual and multi-region search",
  description: "I map your international URL architecture, evaluating whether subdirectories (/de/), subdomains (de.yoursite.com), or ccTLDs (.de) best suit your resources, tech stack, and brand authority. I also plan localised keyword research rather than simple word-for-word translation.",
  keyDeliverables: ["URL Architecture Recommendation", "Market-by-Market Keyword Research Plan", "Cultural Search Nuance Mapping", "Cannibalisation Prevention Strategy"],
  consultantNote: "Direct translation often fails in SEO; I uncover terms native speakers search for in each target country."
}, {
  id: "step-3",
  stepNumber: "03",
  title: "Technical Implementation",
  shortTitle: "Technical Setup",
  timeline: "Scope dependent",
  icon: Code2,
  tagline: "Hreflang markup and crawlable language architecture",
  description: "Sound technical setup helps search engines understand which version to serve to users in every country. I guide the implementation of reciprocal hreflang tags, self-canonical URLs, per-version XML sitemaps, and crawlable language switchers.",
  keyDeliverables: ["Hreflang Specification & Validation", "Geo-Specific XML Sitemap Clusters", "CDN and Server Performance Review", "Crawlable Language Switcher Guidelines"],
  consultantNote: "Hreflang annotations and canonical tags must be consistent across all language alternatives to avoid search engine confusion."
}, {
  id: "step-4",
  stepNumber: "04",
  title: "Content Localisation & Authority Development",
  shortTitle: "Content & Authority",
  timeline: "Scope dependent",
  icon: FileText,
  tagline: "Authentic localised content and regional relevance",
  description: "I prepare detailed SEO briefs for native translators and copywriters, adapting on-page messaging, currency, units, and tone for local audiences. Alongside content, I identify regional digital PR, relevant local publications, and industry associations to establish trusted domestic relevance.",
  keyDeliverables: ["Native-Language SEO Content Briefs", "On-Page Localisation Specifications", "Regional Editorial Link Building Strategy", "Local Entity and Citation Guidance"],
  consultantNote: "Local editorial links and native phrasing signal relevance to regional search engines, building credibility with local buyers."
}, {
  id: "step-5",
  stepNumber: "05",
  title: "Monitoring & Expansion",
  shortTitle: "Monitoring",
  timeline: "Ongoing",
  icon: LineChart,
  tagline: "Continuous tracking, indexation review & phased scaling",
  description: "International organic performance requires ongoing oversight. I monitor search traffic, enquiries, and ranking trends segmented by country, resolving crawl conflicts and identifying when your site is ready to expand into additional territories.",
  keyDeliverables: ["Country-by-Country Performance Dashboard", "Hreflang and Indexation Monitoring", "Regional Conversion Rate Analysis", "Phased Market Expansion Recommendations"],
  consultantNote: "I continuously track regional indexation, review country-level conversions, and identify opportunities for phased expansion."
}];
export default function Process() {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const currentStep = processSteps[activeStepIndex];
  return <section id="internationalseoprocess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60 transition-colors">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs sm:text-sm">
            <Sparkles className="size-3.5 mr-1 text-primary" />
            Strategic Delivery Framework
          </Badge>
          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl font-default">
            My International SEO Process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Expanding into international markets requires structured planning, native search insight, and clean technical architecture. Here is how I guide your overseas search growth step by step.
          </p>
        </div>

        {/* Mobile Horizontal Tabs */}
        <div className="mb-8 block lg:hidden">
          <div className="flex overflow-x-auto pb-4 gap-2 scrollbar-none">
            {processSteps.map((step, index) => {
            const IconComponent = step.icon;
            const isActive = index === activeStepIndex;
            return <Button key={step.id} data-index={index} onClick={() => setActiveStepIndex(index)} variant={isActive ? "default" : "outline"} size="sm" className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 ${isActive ? "shadow-xs" : ""}`}>
                  <span className="text-xs font-mono font-bold">{step.stepNumber}</span>
                  <IconComponent className="size-4" />
                  <span>{step.shortTitle}</span>
                </Button>;
          })}
          </div>
        </div>

        {/* Desktop Step Buttons (Spans inside Buttons, NO heading tags) */}
        <div className="hidden lg:grid grid-cols-5 gap-4 mb-10">
          {processSteps.map((step, index) => {
          const IconComponent = step.icon;
          const isActive = index === activeStepIndex;
          return <Button key={step.id} data-index={index} onClick={() => setActiveStepIndex(index)} variant="ghost" className={`cursor-pointer group relative p-4 rounded-xl border transition-all duration-200 flex flex-col justify-between h-auto text-left ${isActive ? "bg-card border-primary ring-2 ring-primary/20 shadow-md" : "bg-background border-border hover:border-primary/50 hover:bg-muted/50"}`}>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded-md ${isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {step.stepNumber}
                    </span>
                    <Badge variant="outline" className="text-[11.5px] px-1.5 py-0 font-normal">
                      <Clock className="size-2.5 mr-1" />
                      {step.timeline}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-2 rounded-lg ${isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground group-hover:text-foreground"}`}>
                      <IconComponent className="size-4" />
                    </div>
                    <span className="text-xs font-semibold text-foreground line-clamp-2">
                      {step.title}
                    </span>
                  </div>
                </div>

                <div className="mt-3 w-full bg-muted h-1 rounded-full overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${isActive ? "bg-primary w-full" : index < activeStepIndex ? "bg-primary/50 w-full" : "w-0"}`} />
                </div>
              </Button>;
        })}
        </div>

        {/* Active Step Card */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div key={currentStep.id} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border border-border shadow-lg bg-card overflow-hidden">
                <div className="bg-primary/5 border-b border-border p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary text-primary-foreground rounded-xl shadow-xs">
                      {React.createElement(currentStep.icon, {
                      className: "size-7"
                    })}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-mono font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-md">
                          Phase {currentStep.stepNumber}
                        </span>
                        <Badge variant="outline" className="bg-background">
                          <Clock className="size-3 mr-1 text-muted-foreground" />
                          Timing: {currentStep.timeline}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
                        <h3>{currentStep.title}</h3>
                      </CardTitle>
                      <p className="text-sm font-medium text-accent-foreground/80 mt-1">
                        {currentStep.tagline}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 self-start md:self-auto flex-wrap">
                    <Button variant="outline" size="sm" disabled={activeStepIndex === 0} onClick={() => setActiveStepIndex(prev => Math.max(0, prev - 1))} className="rounded-lg">
                      Previous Step
                    </Button>
                    <Button variant="outline" size="sm" disabled={activeStepIndex === processSteps.length - 1} onClick={() => setActiveStepIndex(prev => Math.min(processSteps.length - 1, prev + 1))} className="rounded-lg">
                      Next Step <ChevronRight className="size-4" />
                    </Button>
                  </div>
                </div>

                <CardContent className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                  <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-mono block">
                        Overview &amp; Execution Strategy
                      </span>
                      <p className="text-base text-foreground leading-relaxed font-default">
                        {currentStep.description}
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-muted/60 border border-border flex items-start gap-3">
                      <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-bold text-foreground block uppercase font-mono mb-0.5">
                          Consultant Advisory
                        </span>
                        <p className="text-sm text-muted-foreground leading-normal">
                          {currentStep.consultantNote}
                        </p>
                      </div>
                    </div>

                    <div className="pt-2 flex flex-wrap items-center gap-4">
                      <Button asChild size="lg" className="rounded-lg font-medium shadow-xs">
                        <Link to="/ContactPage">
                          Book a Free SEO Consultation
                          <ArrowRight className="size-4 ml-1" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="lg" className="rounded-lg font-medium">
                        <Link to="/CaseStudiesPage">
                          Explore Case Studies
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="lg:col-span-5 bg-background p-6 rounded-xl border border-border shadow-2xs">
                    <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                      <Layers className="size-4 text-primary" />
                      <span className="text-sm font-bold text-foreground uppercase tracking-wide font-mono">
                        Key Deliverables
                      </span>
                    </div>

                    <ul className="space-y-3.5">
                      {currentStep.keyDeliverables.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span className="font-medium leading-snug">{item}</span>
                        </li>)}
                    </ul>

                    <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                      <span>Led by</span>
                      <span className="font-semibold text-foreground">Yassine Essaadi</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2 lg:hidden">
          {processSteps.map((_, idx) => <Button key={idx} data-index={idx} onClick={() => setActiveStepIndex(idx)} variant="ghost" size="sm" className={`h-2 rounded-full transition-all px-0 py-0 ${idx === activeStepIndex ? "w-8 bg-primary" : "w-2 bg-muted hover:bg-muted-foreground/30"}`} aria-label={`Go to step ${idx + 1}`} />)}
        </div>
      </div>
    </section>;
}